import React from 'react';
import cn from 'clsx';

import Button from '../../library/button/button';

import {
  useAddProductToCart,
  useMyAccount,
  useTireWheelPackage
} from '../../shared/hooks/hooks';

import { formatCurrency } from '../../shared/utils/format/format';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import {
  getPrebuiltSubtotal,
  getProductSubtotal
} from './utils/add-product-button-utils';

import { ProductItem } from '../../shared/types/product';

import styles from './add-product-button.scss';

export interface AddProductButtonProps {
  children?: React.ReactNode;
  className?: string;
  hideSubtotal?: boolean;
  icon?: React.ElementType;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isSecondary?: boolean;
  onClick?: (productItem: ProductItem) => void;
  onProductAdded?: () => void;
  productItem: ProductItem;
  storeCode?: string | null;
}

function AddProductButton(props: AddProductButtonProps) {
  const {
    children,
    className,
    hideSubtotal = false,
    icon,
    isDisabled = false,
    isFullWidth = false,
    isSecondary = false,
    onClick,
    onProductAdded,
    productItem,
    storeCode
  } = props;
  const {
    prebuiltPackage,
    product,
    productSet,
    productSetQuantity,
    quantity,
    rearProduct,
    rearQuantity
  } = productItem || {};
  const { code: productCode, price } = product || productSet?.[0] || {};
  const { addProduct, isAddingProduct } = useAddProductToCart({
    onProductAdded
  });

  const { currentTireWheelPackageFlow } = useTireWheelPackage();
  const { isFleetAccount } = useMyAccount();

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const shouldAddToPackage = Boolean(currentTireWheelPackageFlow);
  const defaultText = shouldAddToPackage ? 'Add to Package' : 'Add to Cart';

  const hideSeePriceInCart =
    prebuiltPackage?.product1?.mapDisplayRule?.mapPriceDisplay ||
    prebuiltPackage?.product1?.mapRuleSatisfied ||
    prebuiltPackage?.product2?.mapDisplayRule?.mapPriceDisplay ||
    prebuiltPackage?.product2?.mapRuleSatisfied;

  let finalSubtotal;

  if (isArrayWithLength(productSetQuantity) && isArrayWithLength(productSet)) {
    finalSubtotal = productSet!.reduce((subtotal, product, index) => {
      subtotal += getProductSubtotal(
        productSetQuantity![index],
        product,
        isFleetEnabled
      );

      return subtotal;
    }, 0);
  } else if (quantity) {
    if (prebuiltPackage) {
      finalSubtotal = getPrebuiltSubtotal(
        quantity,
        prebuiltPackage,
        isFleetEnabled
      );
    } else {
      let frontSubtotal;
      let rearSubtotal;

      if (product) {
        frontSubtotal = getProductSubtotal(quantity, product, isFleetEnabled);
      }

      if (rearProduct && rearQuantity) {
        rearSubtotal = getProductSubtotal(
          rearQuantity,
          rearProduct,
          isFleetEnabled
        );
      }

      finalSubtotal = frontSubtotal + (rearSubtotal ?? 0);
    }
  }

  return (
    <Button
      className={cn(
        styles['button'],
        `${className ? `${className} ` : ''}add-product-btn${
          shouldAddToPackage ? ' add-product-btn--package' : ''
        }`
      )}
      data-testid="add-product-button"
      icon={icon}
      isDisabled={
        (!prebuiltPackage && !productCode) ||
        (!prebuiltPackage && !price) ||
        (!quantity && !rearQuantity && !productSetQuantity) ||
        isAddingProduct ||
        isDisabled
      }
      isFullWidth={isFullWidth}
      isPrimary={!isSecondary}
      isSecondary={isSecondary}
      onClick={e => {
        e?.preventDefault();
        e?.stopPropagation();

        if (onClick) {
          onClick(productItem);
        }

        addProduct({ productItem, shouldChangeStoreOverride: true, storeCode });
      }}
    >
      {children || defaultText}
      {finalSubtotal && !hideSubtotal && !hideSeePriceInCart
        ? ` (${formatCurrency(finalSubtotal)})`
        : null}
    </Button>
  );
}

export default AddProductButton;
